import React, { useState } from 'react';
import LegalLayout from '../components/layout/LegalLayout';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', muted:'#7A7570', border:'#E8E4DC', white:'#FFFFFF',
};

// ─── Live preference manager ──────────────────────────────────────────────────
const PreferenceManager: React.FC = () => {
  const saved = (() => {
    try { return JSON.parse(localStorage.getItem('Jötr_cookie_consent') || 'null'); } catch { return null; }
  })();

  const [prefs, setPrefs] = useState({ analytics: saved?.analytics ?? true, marketing: saved?.marketing ?? false });
  const [saved2, setSaved2] = useState(false);

  const save = () => {
    localStorage.setItem('Jötr_cookie_consent', JSON.stringify({ essential:true, ...prefs, ts: Date.now() }));
    setSaved2(true);
    setTimeout(() => setSaved2(false), 2500);
  };

  const categories = [
    { key:'essential', label:'Essential cookies',  locked:true,  active:true,
      desc:'Required for Jötr to function — login sessions, CSRF tokens, theme preference. Cannot be disabled.' },
    { key:'analytics', label:'Analytics cookies',  locked:false, active:prefs.analytics,
      desc:'Anonymised page view data via Plausible Analytics. No cross-site tracking, no user profiles.' },
    { key:'marketing', label:'Marketing cookies',  locked:false, active:prefs.marketing,
      desc:'Used to measure campaign effectiveness. Currently not active on the free plan.' },
  ];

  return (
    <>
      <style>{`
        .pref-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          padding: 16px 0;
          border-bottom: 1px solid #E8E4DC;
        }
        .pref-toggle {
          width: 44px;
          height: 24px;
          border-radius: 12px;
          position: relative;
          transition: background .2s;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .pref-toggle-knob {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #fff;
          position: absolute;
          top: 3px;
          transition: left .2s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
        }
        @media (max-width: 480px) {
          .pref-row { flex-wrap: wrap; gap: 12px; }
          .pref-toggle { margin-top: 0; }
        }
      `}</style>
      <div style={{ background:C.amber100, border:`1px solid ${C.amber200}`, borderRadius:16, padding:'clamp(18px,4vw,28px)', marginTop:32 }}>
        <p style={{ fontSize:14, fontWeight:700, color:C.ink, fontFamily:"'Sora',sans-serif", marginBottom:4 }}>
          Your cookie preferences
        </p>
        <p style={{ fontSize:13, color:C.muted, fontFamily:"'DM Sans',sans-serif", marginBottom:20, lineHeight:1.6 }}>
          {saved
            ? `Last updated: ${new Date(saved.ts).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}`
            : 'You have not set preferences yet.'}
        </p>

        {categories.map(cat => (
          <div key={cat.key} className="pref-row">
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                <span style={{ fontSize:14, fontWeight:600, color:C.ink, fontFamily:"'DM Sans',sans-serif" }}>{cat.label}</span>
                {cat.locked && (
                  <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20, background:C.amber200, color:C.amber400, fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap' }}>
                    Always on
                  </span>
                )}
              </div>
              <p style={{ fontSize:13, color:C.muted, fontFamily:"'DM Sans',sans-serif", lineHeight:1.6, margin:0 }}>
                {cat.desc}
              </p>
            </div>
            {/* Toggle */}
            <div
              className="pref-toggle"
              style={{ background: cat.active ? C.amber300 : C.border, cursor: cat.locked ? 'not-allowed' : 'pointer' }}
              onClick={() => { if (!cat.locked) setPrefs(p => ({ ...p, [cat.key]: !p[cat.key as keyof typeof p] })); }}
            >
              <div className="pref-toggle-knob" style={{ left: cat.active ? 23 : 3 }}/>
            </div>
          </div>
        ))}

        <div style={{ marginTop:20, display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
          <button
            onClick={save}
            style={{ padding:'10px 24px', borderRadius:9, border:'none', cursor:'pointer', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:14, fontFamily:"'DM Sans',sans-serif" }}
          >
            Save preferences
          </button>
          {saved2 && (
            <span style={{ fontSize:13, color:'#16A34A', fontWeight:600, fontFamily:"'DM Sans',sans-serif" }}>
              ✓ Saved!
            </span>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const CookiesPage: React.FC = () => (
  <LegalLayout
    title="Cookie Policy"
    subtitle="This policy explains what cookies are, which ones we use, and how you can control them."
    lastUpdated="March 22, 2026"
  >
    {/* Responsive table wrapper styles injected here */}
    <style>{`
      .cookie-table-wrap {
        width: 100%;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        border-radius: 10px;
        border: 1px solid #E8E4DC;
        margin: 20px 0;
      }
      .cookie-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;
        min-width: 480px;
      }
      .cookie-table th {
        background: #FFF4E0;
        color: #1A1814;
        font-weight: 700;
        padding: 11px 16px;
        text-align: left;
        font-family: 'DM Sans', sans-serif;
        border-bottom: 1px solid #E8E4DC;
        white-space: nowrap;
      }
      .cookie-table td {
        padding: 11px 16px;
        border-bottom: 1px solid #E8E4DC;
        color: #3D3A34;
        font-family: 'DM Sans', sans-serif;
        vertical-align: top;
        line-height: 1.5;
      }
      .cookie-table tr:last-child td { border-bottom: none; }
      .cookie-table tr:nth-child(even) td { background: #FAFAF8; }
      .cookie-table code {
        background: #FFF4E0;
        color: #C4811A;
        padding: 1px 6px;
        border-radius: 4px;
        font-size: 12px;
        font-family: monospace;
      }
      @media (max-width: 520px) {
        .cookie-table th, .cookie-table td { padding: 9px 12px; font-size: 13px; }
      }
    `}</style>

    <h2>What are cookies?</h2>
    <p>Cookies are small text files placed on your device when you visit a website. They help websites remember information about your visit — like keeping you logged in or remembering your preferences.</p>
    <p>We also use similar technologies including local storage and session storage. This policy covers all of these technologies.</p>

    <h2>Cookies we use</h2>
    <div className="cookie-table-wrap">
      <table className="cookie-table">
        <thead>
          <tr>
            <th>Cookie</th>
            <th>Type</th>
            <th>Purpose</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr><td><code>Jötr_token</code></td><td>Essential</td><td>Keeps you logged in</td><td>7 days</td></tr>
          <tr><td><code>Jötr_cookie_consent</code></td><td>Essential</td><td>Stores your cookie preferences</td><td>1 year</td></tr>
          <tr><td><code>Jötr_theme</code></td><td>Essential</td><td>Remembers dark/light mode</td><td>1 year</td></tr>
          <tr><td><code>_plausible</code></td><td>Analytics</td><td>Anonymised page view tracking</td><td>Session</td></tr>
          <tr><td><code>_ga</code></td><td>Analytics</td><td>Google Analytics (with consent only)</td><td>2 years</td></tr>
        </tbody>
      </table>
    </div>

    <h2>Essential cookies</h2>
    <p>These are required for Jötr to work. Without them you cannot log in or use the app. They cannot be disabled.</p>

    <h2>Analytics cookies</h2>
    <p>We use Plausible Analytics — a privacy-first tool that does not use cross-site tracking, does not build user profiles, and is GDPR compliant. We may also use Google Analytics with your explicit consent.</p>

    <h2>Marketing cookies</h2>
    <p>We currently do not use marketing cookies on the Jötr free plan. If this changes, we will ask for your consent first.</p>

    <h2>Third-party cookies</h2>
    <p>Some features embed content from third parties (e.g. Google OAuth). These providers may set their own cookies governed by their privacy policies.</p>

    <h2>How to control cookies</h2>
    <p>Use the preference manager below to control analytics and marketing cookies. You can also clear all cookies from your browser settings — note that clearing essential cookies will sign you out of Jötr.</p>
    <p>Browser guides:&nbsp;
      <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a> ·&nbsp;
      <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Firefox</a> ·&nbsp;
      <a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a>
    </p>

    <PreferenceManager/>

    <h2>Contact</h2>
    <p>Questions about cookies? Email <a href="mailto:privacy@Jötr.co">privacy@Jötr.co</a>.</p>
  </LegalLayout>
);

export default CookiesPage;